/**
 * The upper/lower body animation layering system (M2).
 *
 * **This is required, not an optimisation.** No combat set in the art library
 * contains locomotion and every locomotion clip is unarmed, so without layering a
 * character cannot swing a sword while running: there is no clip anywhere in the
 * collection of an armed character moving. "Combat in motion" exists only as lower
 * body + upper body + additives, resolved here.
 *
 * What layering is *not* is a property of a clip. Every clip the pipeline ships
 * drives all 51 joints, so **the default way to play one is whole**: a sword swing
 * performed on its own is the whole swing, footwork included. Masking happens only
 * when a caller asks for it, to compose one clip's torso over another's legs.
 *
 * That is why a clip is registered **per role**, not once. The same swing can be
 * bound as `full` for the Animations preview and as `upper` for an attack composed
 * over a run, and the two bindings are separate `AnimationGroup`s over the same
 * shared curves. Binding a clip once under whichever role asked first -- what this
 * used to do -- is what silently threw away the legs of every combat clip.
 *
 * Built once, for every character. Classes differ only in which clips feed the upper
 * layer -- nothing in this file knows what a swordsman is.
 *
 * The mechanism is deliberately plain Babylon: an `AnimationGroup` is a list of
 * `(animation, target)` pairs, so a masked group is just a rebuild of that list
 * filtered to one bone set (see `animationBinder.ts`). Two masked groups then play
 * at once, driving disjoint bones, and weights blend clips *within* a layer. No
 * custom skinning, no shader work.
 *
 *   lower layer   legs and hips                        Pelvis, spine_01 and below
 *   upper layer   the torso clip composed over them    spine_02 up, arms, hands, props
 *   full layer    whole-body clips                     outvotes both of the above
 *   additive      aim offsets, lean, head look         applied after the base resolves
 */
import { AnimationGroup } from '@babylonjs/core/Animations/animationGroup';
import type { Scene } from '@babylonjs/core/scene';
import type { Skeleton } from '@babylonjs/core/Bones/skeleton';
import { LAYER_BLEND } from '@dungeon-builder/shared';

import { loadClip, loadClipBundle, type BoundClip } from './animationBinder.ts';
import { resolveBoneMasks, type ResolvedBoneMasks } from './boneMasks.ts';

/** The three layers a clip can be played on. `full` outvotes the other two. */
export type LayerName = 'lower' | 'upper' | 'full';
/** How a clip is bound: to one mask, to the whole body, or as an offset. */
export type ClipRole = LayerName | 'additive';

export interface RegisterOptions {
  role: ClipRole;
  /**
   * Animation name inside the file, when it holds more than one. Packs ship clips in
   * bundles, so this is how one is picked out.
   */
  animationName?: string;
}

export interface PlayOptions {
  blendMs?: number;
  loop?: boolean;
  speed?: number;
  /** Called when a non-looping clip reaches its end. */
  onComplete?: () => void;
}

/**
 * The group name a role is bound under.
 *
 * Distinct per role because the same clip may hold several bindings at once, and two
 * groups sharing a name are indistinguishable in the inspector and in tests. `full`
 * keeps the bare clip id: it is the default, and the id is what a reader expects.
 */
function groupName(id: string, role: ClipRole): string {
  return role === 'full' ? id : `${id}:${role}`;
}

function key(id: string, role: ClipRole): string {
  // `|` cannot appear in a clip id (`family/group/name`), so the two halves of the
  // key can never be confused for one another.
  return `${id}|${role}`;
}

interface Fade {
  clip: BoundClip;
  /**
   * A lower clip's *upper body*, bound to the upper mask.
   *
   * Without it the seam dial is meaningless: masking the legs clip out of the torso
   * entirely means an upper weight below 1 blends the spine towards nothing at all
   * rather than towards the character's own motion. Carrying the shadow lets
   * `upperWeight` blend between the legs clip's torso and the composed torso, which
   * is what entering and leaving a composition actually needs.
   */
  shadow?: BoundClip;
  /** Weight this entry is heading towards. */
  target: number;
  weight: number;
}

interface LayerState {
  /** Newest first: entry 0 is fading in, the rest are fading out. */
  fades: Fade[];
  /** Scales the whole layer -- the seam control for `upper`. */
  gain: number;
  blendMs: number;
}

function emptyLayer(): LayerState {
  return { fades: [], gain: 1, blendMs: LAYER_BLEND.blendMs };
}

export class AnimationLayerRig {
  readonly masks: ResolvedBoneMasks;

  /** Keyed by clip id *and* role: one clip may hold several bindings at once. */
  private readonly clips = new Map<
    string,
    { clip: BoundClip; role: ClipRole; shadow?: BoundClip }
  >();
  private readonly layers: Record<LayerName, LayerState> = {
    lower: emptyLayer(),
    upper: emptyLayer(),
    full: emptyLayer(),
  };
  private readonly additives = new Map<string, { clip: BoundClip; weight: number }>();
  private readonly tick: () => void;

  /**
   * Weight the upper layer resolves to when nothing is overriding it.
   *
   * Where the seam itself sits is a bone-mask question, settled in `rigMap.json`
   * (`Pelvis` and `spine_01` below it). This dial is the softer control on top: how
   * much of the torso a composition claims while it is asserted.
   */
  private upperWeight: number = LAYER_BLEND.upperWeight;

  constructor(
    private readonly scene: Scene,
    private readonly skeleton: Skeleton,
  ) {
    this.masks = resolveBoneMasks(skeleton);
    this.tick = () => this.advance(this.scene.getEngine().getDeltaTime());
    scene.onBeforeRenderObservable.add(this.tick);
  }

  /** Bones no layer drives -- expected to be the root, the sockets and the capes. */
  get undriven(): string[] {
    return this.masks.unclaimed;
  }

  private boneFilterFor(role: ClipRole): ((bone: string) => boolean) | undefined {
    if (role === 'lower') {
      const lower = new Set(this.masks.lower);
      return (bone: string) => lower.has(bone);
    }
    if (role === 'upper') {
      const upper = new Set(this.masks.upper);
      return (bone: string) => upper.has(bone);
    }
    // `full` and `additive` drive everything the clip drives.
    return undefined;
  }

  async register(id: string, url: string, options: RegisterOptions): Promise<void> {
    const { role } = options;
    if (this.clips.has(key(id, role))) return;

    const boneFilter = this.boneFilterFor(role);
    const name = groupName(id, role);

    // An additive is differenced in place, so it must not share the bundle's curves
    // with the next character to register the same clip.
    const clone = role === 'additive';
    const clip = options.animationName
      ? (
          await loadClipBundle(this.scene, url, this.skeleton, [
            { key: name, name: options.animationName, boneFilter, clone },
          ])
        ).get(name)
      : await loadClip(this.scene, url, this.skeleton, { name, boneFilter, clone });
    if (!clip) throw new Error(`${url}: no animation named "${options.animationName}"`);

    // A lower-layer clip is bound a second time against the upper mask, so the torso
    // has something of its own to blend with when no composition is asserting itself
    // -- see `Fade.shadow`.
    let shadow: BoundClip | undefined;
    if (role === 'lower') {
      const upper = new Set(this.masks.upper);
      const shadowName = `${id}:shadow`;
      shadow = options.animationName
        ? (
            await loadClipBundle(this.scene, url, this.skeleton, [
              {
                key: shadowName,
                name: options.animationName,
                boneFilter: (bone) => upper.has(bone),
              },
            ])
          ).get(shadowName)
        : await loadClip(this.scene, url, this.skeleton, {
            name: shadowName,
            boneFilter: (bone) => upper.has(bone),
          });
      shadow?.group.stop();
    }

    if (role === 'additive') {
      // Converts the group's values to offsets from its own first frame, which is
      // the neutral pose in every additive clip the library ships. Safe to do in
      // place only because the clip was requested with `clone`, so these curves
      // belong to this character and not to the shared bundle.
      AnimationGroup.MakeAnimationAdditive(clip.group, 0);
    }

    clip.group.stop();
    this.clips.set(key(id, role), { clip, role, shadow });
  }

  /**
   * Registers many clips from one bundle, parsing the file once.
   *
   * `register` per clip would re-parse the whole bundle for each -- eight times for a
   * stance bundle of eight clips. A pack ships clips grouped precisely so that does
   * not have to happen.
   *
   * An entry may name a role the clip is already bound under; that one is skipped and
   * the rest are bound, so asking for a second role later costs one more group and
   * no re-parse.
   */
  async registerBundle(url: string, entries: { id: string; role: ClipRole }[]): Promise<number> {
    const pending = entries.filter((entry) => !this.clips.has(key(entry.id, entry.role)));
    if (pending.length === 0) return 0;

    const upper = new Set(this.masks.upper);
    const requests = pending.flatMap((entry) => {
      const own = {
        key: groupName(entry.id, entry.role),
        name: entry.id,
        boneFilter: this.boneFilterFor(entry.role),
        // See `ClipRequest.clone`: `MakeAnimationAdditive` rewrites the curves.
        clone: entry.role === 'additive',
      };
      // A lower clip is also bound against the upper mask, so the torso has something
      // of its own to blend with -- see `Fade.shadow`.
      return entry.role === 'lower'
        ? [
            own,
            { key: `${entry.id}:shadow`, name: entry.id, boneFilter: (b: string) => upper.has(b) },
          ]
        : [own];
    });

    const bound = await loadClipBundle(this.scene, url, this.skeleton, requests);

    let registered = 0;
    for (const entry of pending) {
      const clip = bound.get(groupName(entry.id, entry.role));
      if (!clip) continue;
      if (entry.role === 'additive') {
        AnimationGroup.MakeAnimationAdditive(clip.group, 0);
      }
      clip.group.stop();
      const shadow = bound.get(`${entry.id}:shadow`);
      shadow?.group.stop();
      this.clips.set(key(entry.id, entry.role), { clip, role: entry.role, shadow });
      registered += 1;
    }
    return registered;
  }

  /**
   * The `AnimationGroup`s one binding drives: its own, and -- for a lower clip -- the
   * torso shadow bound alongside it.
   *
   * Exposed because *transport* is not a layering question and this class has nothing
   * to say about it. A fade decides how much of a clip is heard; where in the clip the
   * needle sits is a separate axis, and a tool that scrubs or slows a clip down needs
   * the groups themselves. Empty when the clip is not registered under that role.
   */
  groupsFor(id: string, role: ClipRole): AnimationGroup[] {
    const entry = this.clips.get(key(id, role));
    if (!entry) return [];
    return entry.shadow ? [entry.clip.group, entry.shadow.group] : [entry.clip.group];
  }

  /**
   * Every group the rig currently has running -- each layer's live fades, their
   * shadows, and the asserted additives.
   *
   * `isStarted` rather than `isPlaying`, because a paused group is still one of these:
   * a transport that dropped what it had paused could never resume it.
   *
   * What holding the *whole* character still needs. Pausing only the clip being
   * watched leaves an additive's base pose walking on underneath it, which reads as
   * the pause button having done nothing at all.
   */
  activeGroups(): AnimationGroup[] {
    const groups: AnimationGroup[] = [];
    for (const layer of ['lower', 'upper', 'full'] as const) {
      for (const fade of this.layers[layer].fades) {
        groups.push(fade.clip.group);
        if (fade.shadow) groups.push(fade.shadow.group);
      }
    }
    for (const { clip } of this.additives.values()) groups.push(clip.group);
    return groups.filter((group) => group.isStarted);
  }

  /** Whether `id` is bound -- under `role` if one is given, under any role if not. */
  has(id: string, role?: ClipRole): boolean {
    if (role) return this.clips.has(key(id, role));
    return (['lower', 'upper', 'full', 'additive'] as const).some((candidate) =>
      this.clips.has(key(id, candidate)),
    );
  }

  /**
   * Crossfades `id` into `layer`. Passing null fades the layer out.
   *
   * The clip must be registered under the role of the same name: `play('upper', id)`
   * uses the torso-masked binding, `play('full', id)` the whole-body one. They are
   * different groups over the same curves, so asking for the wrong one is a
   * programming error rather than something to paper over.
   */
  play(layer: LayerName, id: string | null, options: PlayOptions = {}): void {
    const state = this.layers[layer];
    state.blendMs = options.blendMs ?? LAYER_BLEND.blendMs;

    if (id === null) {
      for (const fade of state.fades) fade.target = 0;
      this.applyWeights();
      return;
    }

    const entry = this.clips.get(key(id, layer));
    if (!entry) {
      throw new Error(`AnimationLayerRig: clip "${id}" is not registered for the ${layer} layer`);
    }

    // Re-requesting the clip already on this layer must revive it, not stack a second
    // fade on top. Two fades sharing one AnimationGroup fight over its weight, and the
    // outgoing one eventually stops the group the incoming one is still using -- so
    // asking for the state you are already in would silently switch the layer off.
    // A game loop calls `play` with the current state constantly, so this is the
    // common path, not an edge case.
    const running = state.fades.find((fade) => fade.clip === entry.clip);
    if (running) {
      for (const fade of state.fades) fade.target = fade === running ? 1 : 0;
      state.fades = [running, ...state.fades.filter((fade) => fade !== running)];
      // A one-shot asked for again while it is still fading out has already *ended*:
      // its group is stopped and parked on its last frame, so reviving the fade alone
      // would bring back a still image at full weight. Start it over instead.
      if (!running.clip.group.isPlaying) {
        this.start(running, options, layer, state);
      }
      this.applyWeights();
      return;
    }

    for (const fade of state.fades) fade.target = 0;

    const fade: Fade = { clip: entry.clip, shadow: entry.shadow, target: 1, weight: 0 };
    state.fades.unshift(fade);
    this.start(fade, options, layer, state);
    this.applyWeights();
  }

  /**
   * Runs a fade's groups from the top.
   *
   * Split out because starting is not the same as scheduling: a fade is started when
   * it joins a layer, and started *again* when a finished one-shot is asked for
   * before it has faded away.
   */
  private start(fade: Fade, options: PlayOptions, layer: LayerName, state: LayerState): void {
    const loop = options.loop ?? true;
    const group = fade.clip.group;
    group.start(loop, options.speed ?? 1);
    if (fade.shadow) {
      fade.shadow.group.start(loop, options.speed ?? 1);
      // Both halves of the same clip must sit on the same frame, or the torso lags
      // the legs by whatever the two groups drifted apart.
      fade.shadow.group.syncAllAnimationsWith(group.animatables[0] ?? null);
    }

    if (!loop) {
      group.onAnimationGroupEndObservable.addOnce(() => {
        // A whole-body clip hands the body back when it ends -- a stagger releases the
        // legs it took. A masked one holds its last frame until its caller fades it
        // out, which is what lets a one-shot attack settle into a guard.
        if (layer === 'full' && state.fades.includes(fade)) fade.target = 0;
        options.onComplete?.();
      });
    }
  }

  /**
   * Plays a clip across both base layers at once.
   *
   * A character struck mid-run plays the stagger with its whole body, not from the
   * waist up. Kept as a name of its own because "this clip owns everything" reads
   * better at the call site than the layer it happens to use.
   */
  playOverride(id: string, options: PlayOptions = {}): void {
    this.play('full', id, { loop: false, ...options });
  }

  /** Releases the whole-body layer, letting the base layers resolve again. */
  clearOverride(): void {
    this.play('full', null);
  }

  /** Sets an additive offset's contribution, 0 to disable. */
  setAdditive(id: string, weight: number): void {
    const entry = this.clips.get(key(id, 'additive'));
    if (!entry) throw new Error(`AnimationLayerRig: clip "${id}" is not registered as an additive`);

    const existing = this.additives.get(id);
    if (weight <= 0) {
      if (existing) {
        existing.clip.group.stop();
        this.additives.delete(id);
      }
      return;
    }
    if (!existing) {
      entry.clip.group.start(true, 1);
      this.additives.set(id, { clip: entry.clip, weight });
    } else {
      existing.weight = weight;
    }
    entry.clip.group.weight = weight;
  }

  /** The seam control: how strongly the upper layer asserts itself. */
  setUpperWeight(weight: number): void {
    this.upperWeight = Math.max(0, Math.min(1, weight));
    this.applyWeights();
  }

  getUpperWeight(): number {
    return this.upperWeight;
  }

  private advance(deltaMs: number): void {
    let changed = false;

    for (const layer of ['lower', 'upper', 'full'] as const) {
      const state = this.layers[layer];
      const step = state.blendMs > 0 ? deltaMs / state.blendMs : 1;
      for (const fade of state.fades) {
        const next = fade.weight + Math.sign(fade.target - fade.weight) * step;
        const clamped =
          fade.target > fade.weight ? Math.min(next, fade.target) : Math.max(next, fade.target);
        if (clamped !== fade.weight) changed = true;
        fade.weight = clamped;
      }
      // Retire anything that has finished fading out. Zero the weight before stopping
      // so a stopped group never reports a stale contribution.
      const done = state.fades.filter((fade) => fade.target === 0 && fade.weight <= 0);
      for (const fade of done) {
        fade.clip.group.weight = 0;
        fade.clip.group.stop();
        if (fade.shadow) {
          fade.shadow.group.weight = 0;
          fade.shadow.group.stop();
        }
      }
      if (done.length > 0) {
        state.fades = state.fades.filter((fade) => !done.includes(fade));
        changed = true;
      }
    }

    if (changed) this.applyWeights();
  }

  private applyWeights(): void {
    // The whole-body layer does not stop the base layers, it outvotes them: both drive
    // the same bones, and Babylon blends them by weight. Scaling the base layers down
    // by what `full` currently claims is what makes a stagger take the whole body and
    // then hand it back.
    const fullState = this.layers.full;
    const claimedFull = Math.min(
      1,
      fullState.fades.reduce((sum, fade) => sum + fade.weight, 0) * fullState.gain,
    );
    const base = 1 - claimedFull;

    // How much of the torso the composed layer is *currently* claiming -- its live
    // faded weight, not its target. Reading the target instead would hand the upper
    // layer the whole torso the instant a composition was requested, while its clip
    // was still at weight zero, leaving the spine driven by almost nothing for the
    // length of the blend. Using the live value makes entering and leaving a
    // composition a real crossfade: the torso clip rises exactly as the legs clip's
    // own torso gives way.
    const upperState = this.layers.upper;
    const claimed = upperState.fades.reduce((sum, fade) => sum + fade.weight, 0);
    const seam = Math.min(1, claimed * upperState.gain) * this.upperWeight;

    for (const fade of this.layers.lower.fades) {
      const weight = fade.weight * this.layers.lower.gain * base;
      fade.clip.group.weight = weight;
      // The two halves of a legs clip share the torso with the composed layer:
      // together they always sum to one full pose, so the spine is never driven by
      // less than a whole animation.
      if (fade.shadow) fade.shadow.group.weight = weight * (1 - seam);
    }

    for (const fade of upperState.fades) {
      fade.clip.group.weight = fade.weight * upperState.gain * base * this.upperWeight;
    }

    for (const fade of fullState.fades) {
      fade.clip.group.weight = fade.weight * fullState.gain;
    }
  }

  dispose(): void {
    this.scene.onBeforeRenderObservable.removeCallback(this.tick);
    for (const { clip, shadow } of this.clips.values()) {
      clip.dispose();
      shadow?.dispose();
    }
    this.clips.clear();
    this.additives.clear();
    this.layers.lower = emptyLayer();
    this.layers.upper = emptyLayer();
    this.layers.full = emptyLayer();
  }
}
